import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import * as AppPropTypes from '../lib/PropTypes';

import { useUrlGenerator } from './RoutesContext';

const SiteContext = React.createContext(null);

export const useSiteContext = () => useContext(SiteContext);

export const useWindowSize = () => {
    const { windowSize } = useSiteContext();
    return windowSize;
};

export const useMenu = () => {
    const {
        menus,
        menuOpen = false,
        setMenuOpen,
        openMenu,
        closeMenu,
        menuHidden,
        hideMenu,
        showMenu,
    } = useSiteContext();
    return { ...menus, menuOpen, setMenuOpen, openMenu, closeMenu, menuHidden, hideMenu, showMenu };
};

export const useSearchBar = () => {
    const { searchOpen = false, setSearchOpen, openSearch, closeSearch } = useSiteContext();
    return { searchOpen, setSearchOpen, openSearch, closeSearch };
};

export const useLocales = () => {
    const { locales } = useSiteContext();
    return locales;
};

export const useVideoState = () => {
    const { videosMuted, setMuted } = useSiteContext();
    return { muted: videosMuted, setMuted };
};

export const useCurrentLocale = () => {
    const route = useUrlGenerator();
    const { locale } = useIntl();
    const locales = useLocales();
    const current = useMemo(() => locales.find((it) => it === locale), [route, locales, locale]);
    return current;
};

export const useAlternateLocale = () => {
    const route = useUrlGenerator();
    const { locale } = useIntl();
    const locales = useLocales();
    const items = useMemo(
        () =>
            locales.map((it) => ({
                id: it,
                label: it,
                url: route(`${it}.home`),
                external: true,
                target: '_self',
                rel: null,
                active: it === locale,
            })),
        [route, locales, locale],
    );
    const alternates = items.filter((i) => !i.active);

    return alternates[0];
};

const propTypes = {
    locales: PropTypes.arrayOf(PropTypes.string),
    menus: PropTypes.objectOf(AppPropTypes.menus),
    menuOpen: PropTypes.bool,
    menuHidden: PropTypes.bool,
    searchOpen: PropTypes.bool,
    videosMuted: PropTypes.bool,
    children: PropTypes.node.isRequired,
    windowSize: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
    }),
};

const defaultProps = {
    locales: null,
    menus: {},
    menuOpen: false,
    menuHidden: true,
    searchOpen: false,
    videosMuted: true, // Never change this, mobile autoplay will break
    windowSize: {
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    },
};

export const SiteProvider = ({
    locales,
    menus,
    menuOpen: initialMenuOpen,
    menuHidden: initialMenuHidden,
    searchOpen: initialSearchOpen,
    windowSize: initialWindowSize,
    videosMuted: initialVideosMuted,
    children,
}) => {
    const { locale } = useIntl();
    const [menuOpen, setMenuOpen] = useState(initialMenuOpen);
    const [menuHidden, setMenuHidden] = useState(initialMenuHidden);
    const [searchOpen, setSearchOpen] = useState(initialSearchOpen);
    const [windowSize, setWindowSize] = useState(initialWindowSize);
    const [videosMuted, setVideosMuted] = useState(initialVideosMuted);

    const { width: windowWidth = null, height: windowHeight = null } = windowSize || {};
    const setMuted = useCallback(
        (value) => {
            // Dont do it on mobile, it breaks autoplay
            if (windowWidth > 768) {
                setVideosMuted(value);
            }
        },
        [windowWidth, windowHeight, setVideosMuted],
    );

    const openSearch = useCallback(() => {
        setSearchOpen(true);
    }, [setMenuOpen]);

    const closeSearch = useCallback(() => {
        setSearchOpen(false);
    }, [setMenuOpen]);

    const openMenu = useCallback(() => {
        setMenuOpen(true);
    }, [setMenuOpen]);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, [setMenuOpen]);

    const hideMenu = useCallback(() => {
        setMenuHidden(true);
    }, [setMenuHidden]);

    const showMenu = useCallback(() => {
        setMenuHidden(false);
    }, [setMenuHidden]);

    useEffect(() => {
        let id = null;
        if (menuHidden) {
            // Max 5 seconds
            id = setTimeout(() => showMenu(), 5000);
        }
        return () => {
            clearTimeout(id);
        };
    }, [menuHidden, showMenu]);

    useEffect(() => {
        const onResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [setWindowSize]);

    const value = useMemo(
        () => ({
            windowSize,
            menus,
            menuOpen,
            setMenuOpen,
            openMenu,
            closeMenu,
            searchOpen,
            setSearchOpen,
            openSearch,
            closeSearch,
            hideMenu,
            showMenu,
            menuHidden,
            setMuted,
            videosMuted,
            locales: locales || [locale],
        }),
        [
            windowSize,
            menus,
            menuOpen,
            setMenuOpen,
            openMenu,
            closeMenu,
            searchOpen,
            setSearchOpen,
            openSearch,
            closeSearch,
            hideMenu,
            showMenu,
            menuHidden,
            setMuted,
            videosMuted,
            locales,
            locale,
        ],
    );

    return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

SiteProvider.propTypes = propTypes;
SiteProvider.defaultProps = defaultProps;

export default SiteContext;
